import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ref, uploadBytes } from 'firebase/storage'
import { useQueryClient } from '@tanstack/react-query'

import { storage } from '@/firebase'
import downloadImageFromURL from '@/utils/getImageFileFromURL'
import { useDialog } from '@/hooks/ui/useDialog'
import useUtils from '@/hooks/useUtilities'
import { useSections, updateCourse, useDeleteSection } from '@/hooks/useSection'
import { updateUserProfile } from '@/Redux/userSlice'
import langs from '@/assets/langs.json'

export default function useGeneralSettings() {
  const dispatch = useDispatch()
  const { showSnackbar } = useUtils()
  const [loading, setLoading] = useState(false)
  const { profile, auth } = useSelector(({ user }) => user)
  const queryClient = useQueryClient()

  // Profile state
  const [avatar, setAvatar] = useState(profile?.image?.url ?? '')
  const [displayName, setDisplayName] = useState(profile?.displayName ?? '')
  const [language, setLanguage] = useState(profile?.language ?? '')
  const [nativeLanguage, setNativeLanguage] = useState(profile?.nativeLanguage ?? '')
  const [subjects, setSubjects] = useState(profile?.subjects || [])
  const [gradeLevel, setGradeLevel] = useState(profile?.gradeLevel ?? '')
  const [nativeLanguages, setNativeLanguages] = useState([
    { code: profile?.nativeLanguage || 'en', language: langs?.[profile?.nativeLanguage || 'en'] },
    ...(profile?.additionalLanguages || []).map(l => ({ code: l, language: langs[l] })),
  ])
  const [defaultPoints, setDefaultPoints] = useState(profile?.defaultAssignmentPoints ?? 10)
  const [defaultRepeat, setDefaultRepeat] = useState(profile?.defaultAssignmentRepeat ?? 1)
  const [schoolName, setSchoolName] = useState(profile?.schoolName ?? '')
  const [schoolData, setSchoolData] = useState(null)

  // Dialog states
  const [schoolSearchOpen, setSchoolSearchOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const { handleClose, open: openDialog, handleOpen } = useDialog()

  // Classroom management
  const { sections = [] } = useSections('owners')
  const archivedSections = sections.filter(section => section.archived)
  const [selectedClassrooms, setSelectedClassrooms] = useState([])
  const [deleteSectionId, setDeleteSectionId] = useState(null)
  const deleteSectionHook = useDeleteSection(deleteSectionId)

  // Profile change handler
  const handleProfileChange = (field, value) => {
    if (field === 'displayName') setDisplayName(value)
    else if (field === 'language') setLanguage(value)
    else if (field === 'nativeLanguage') setNativeLanguage(value)
    else if (field === 'defaultPoints') setDefaultPoints(value)
    else if (field === 'defaultRepeat') setDefaultRepeat(value)
    else if (field === 'schoolName') setSchoolName(value)
  }

  // Classroom management handlers
  const handleClassroomSelect = id => {
    setSelectedClassrooms(prev => (prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]))
  }

  const handleArchiveToggle = async section => {
    try {
      await updateCourse(section.id, { archived: !section.archived })
      queryClient.invalidateQueries({ queryKey: ['sections', 'owners'] })
      showSnackbar(`${section.name} has been ${section.archived ? 'unarchived' : 'archived'}.`, 'success')
    } catch (e) {
      showSnackbar('Failed to update classroom.', 'error')
    }
  }

  const handleDeleteClassroom = section => {
    setDeleteSectionId(section.id)
  }

  // Avatar handlers
  const handleDeleteAvatar = () => {
    dispatch(updateUserProfile({ image: { url: null, path: null } }))
    setAvatar(null)
  }

  const handleAvatarUpdate = async images => {
    if (images && images[0]) {
      const image = images[0]

      setAvatar(image.url)
      try {
        const file = await downloadImageFromURL(image.url)

        if (file) {
          const storageRef = ref(storage, `profiles/${auth?.uid}`)

          await uploadBytes(storageRef, file)
          showSnackbar('Profile photo updated!', 'success')
        }
      } catch (e) {
        showSnackbar('Failed to update photo.', 'error')
      }
    }
    handleClose()
  }

  // Save handler
  const handleSave = async () => {
    setLoading(true)
    const updates = {
      displayName,
      language,
      nativeLanguage,
      additionalLanguages: nativeLanguages?.slice(1)?.map(l => l?.code) || [],
      image: { url: avatar, path: null },
      subjects,
      defaultAssignmentPoints: Number(defaultPoints),
      defaultAssignmentRepeat: Number(defaultRepeat),
      schoolName,
    }

    await dispatch(updateUserProfile(updates))
    setLoading(false)
    showSnackbar('Profile updated successfully', 'success')
  }

  // School selection handler
  const handleSchoolSelect = school => {
    setSchoolName(school.schoolName)
    setSchoolData(school)
    setSchoolSearchOpen(false)
  }

  // Delete section effect
  useEffect(() => {
    const deleteSection = async () => {
      if (!deleteSectionId) return
      try {
        await deleteSectionHook.deleteSection()
        showSnackbar('Classroom deleted.', 'success')
      } catch (e) {
        showSnackbar('Failed to delete classroom.', 'error')
      } finally {
        setDeleteSectionId(null)
      }
    }

    if (deleteSectionId) {
      deleteSection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSectionId])

  return {
    // State
    loading,
    avatar,
    displayName,
    language,
    nativeLanguage,
    gradeLevel,
    subjects,
    nativeLanguages,
    defaultPoints,
    defaultRepeat,
    schoolName,
    schoolData,
    schoolSearchOpen,
    deleteModalOpen,
    openDialog,
    archivedSections,
    selectedClassrooms,

    // Setters
    setSubjects,
    setGradeLevel,
    setNativeLanguages,
    setSchoolSearchOpen,
    setDeleteModalOpen,

    // Handlers
    handleProfileChange,
    handleClassroomSelect,
    handleArchiveToggle,
    handleDeleteClassroom,
    handleDeleteAvatar,
    handleAvatarUpdate,
    handleSave,
    handleSchoolSelect,
    handleClose,
    handleOpen,
  }
}
